import { useState } from "react";
import api from "../api";
import {
  Field,
  TextInput,
  Actions,
  PrimaryButton,
  ResultBanner,
  BusyNote,
  CardIntro,
} from "./ui/Form";

/**
 * Avery 5160 mailing labels, one DOCX per zone, zipped.
 *
 * The layer IDs are typed rather than hardcoded because the backend also
 * accepts them from environment variables -- see the fallback in
 * /generate-mailer-labels. They are remembered in localStorage so nobody has to
 * paste the same two GUIDs every time.
 */

const REMEMBER_KEY = "pioneer.mailerLabels.layerIds";

function loadRemembered(): { point: string; parcel: string } {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* corrupt or unavailable storage is not worth failing over */
  }
  return { point: "", parcel: "" };
}

export default function MailerLabelsCard() {
  const currentYear = new Date().getFullYear();
  const remembered = loadRemembered();

  const [pointLayerId, setPointLayerId] = useState(remembered.point);
  const [parcelLayerId, setParcelLayerId] = useState(remembered.parcel);
  const [year, setYear] = useState<number>(currentYear);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);

  const ready = pointLayerId.trim().length > 0 && parcelLayerId.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) {
      setResult({ status: "failure", message: "Both layer IDs are required." });
      return;
    }

    setBusy(true);
    setResult(null);

    try {
      const response = await api.post(
        "/generate-mailer-labels",
        {
          point_layer_id: pointLayerId.trim(),
          parcel_layer_id: parcelLayerId.trim(),
          year,
        },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Mailer_Labels_${year}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Only remember IDs that actually worked.
      try {
        localStorage.setItem(
          REMEMBER_KEY,
          JSON.stringify({ point: pointLayerId.trim(), parcel: parcelLayerId.trim() })
        );
      } catch {
        /* non-fatal */
      }

      setResult({
        status: "success",
        message: `Generated ${response.headers["x-total-labels"] ?? "?"} labels — download started.`,
        details: response.headers["x-label-counts"],
      });
    } catch (error: any) {
      // The error arrives as a Blob because the request asked for one.
      let message = "An unexpected error occurred.";
      if (error.response) {
        try {
          const text = await error.response.data.text();
          message = JSON.parse(text).message || text;
        } catch {
          message = `Server error (${error.response.status})`;
        }
      } else if (error.message) {
        message = error.message;
      }
      setResult({ status: "failure", message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CardIntro>
        Finds PointLocation sites flagged <code className="font-mono bg-paper-2 px-1 rounded">Mailer = yes</code>,
        matches each to its parcel owner, and produces one Avery 5160 document per zone.
      </CardIntro>

      <Field label="Label year">
        <TextInput
          type="number"
          value={year}
          min={2000}
          max={2100}
          onChange={(e) => setYear(Number(e.target.value))}
          disabled={busy}
        />
      </Field>

      <Field
        label="PointLocation layer ID"
        hint="ArcGIS Online → the item page → the ID after /items/ in the URL"
      >
        <TextInput
          value={pointLayerId}
          onChange={(e) => setPointLayerId(e.target.value)}
          placeholder="e.g. 49262609faee4515bad3b18b0b854e9b"
          className="font-mono"
          disabled={busy}
        />
      </Field>

      <Field label="CMAD Parcels layer ID" hint="Remembered after the first successful run.">
        <TextInput
          value={parcelLayerId}
          onChange={(e) => setParcelLayerId(e.target.value)}
          placeholder="e.g. b6b77e415e35420199fad947fe49c5bc"
          className="font-mono"
          disabled={busy}
        />
      </Field>

      <Actions>
        <PrimaryButton type="submit" busy={busy} busyLabel="Generating…" disabled={!ready}>
          Generate labels
        </PrimaryButton>
      </Actions>

      {busy && (
        <BusyNote>
          Querying sites and joining to parcels. This usually takes a couple of minutes.
        </BusyNote>
      )}

      <ResultBanner result={result} />
    </form>
  );
}
